import React from "react";
import { useQuery } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";

// Graph wrapper component from AntV
const Graph = (props) => {
  const { options, onRender, onDestroy } = props;
  const graphRef = React.useRef();
  const containerRef = React.useRef(null);

  React.useEffect(() => {
    const { Graph: G6Graph } = require("@antv/g6");
    const graph = new G6Graph({ container: containerRef.current });
    graphRef.current = graph;

    return () => {
      const graph = graphRef.current;
      if (graph) {
        graph.destroy();
        onDestroy?.();
        graphRef.current = undefined;
      }
    };
  }, [onDestroy]);

  React.useEffect(() => {
    const container = containerRef.current;
    const graph = graphRef.current;

    if (!options || !container || !graph || graph.destroyed) return;

    graph.setOptions(options);
    graph
      .render()
      .then(() => onRender?.(graph))
      .catch((error) => console.debug(error));
  }, [options, onRender]);

  // Add touch handling for mobile
  React.useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    let initialDistance = 0;
    let initialZoom = 1;
    let touchCount = 0;
    let lastTap = 0;

    const handleTouchStart = (e) => {
      touchCount = e.touches.length;

      if (e.touches.length === 1) {
        // Single touch - check for double tap
        const currentTime = new Date().getTime();
        const tapLength = currentTime - lastTap;
        if (tapLength < 500 && tapLength > 0) {
          // Double tap detected
          const graph = graphRef.current;
          if (graph) {
            graph.fitView({ padding: 20 });
          }
          e.preventDefault();
        }
        lastTap = currentTime;
      } else if (e.touches.length === 2) {
        // Pinch start
        const touch1 = e.touches[0];
        const touch2 = e.touches[1];
        initialDistance = Math.hypot(
          touch2.clientX - touch1.clientX,
          touch2.clientY - touch1.clientY
        );

        const graph = graphRef.current;
        if (graph) {
          initialZoom = graph.getZoom();
        }
        e.preventDefault();
      }
    };

    const handleTouchMove = (e) => {
      if (e.touches.length === 2) {
        // Pinch zoom
        const touch1 = e.touches[0];
        const touch2 = e.touches[1];
        const currentDistance = Math.hypot(
          touch2.clientX - touch1.clientX,
          touch2.clientY - touch1.clientY
        );

        if (initialDistance > 0) {
          const scale = currentDistance / initialDistance;
          const newZoom = initialZoom * scale;

          const graph = graphRef.current;
          if (graph) {
            // Constrain zoom levels
            const minZoom = 0.1;
            const maxZoom = 3;
            const constrainedZoom = Math.max(
              minZoom,
              Math.min(maxZoom, newZoom)
            );

            // Get center point between touches for zoom origin
            const centerX = (touch1.clientX + touch2.clientX) / 2;
            const centerY = (touch1.clientY + touch2.clientY) / 2;

            // Convert to graph coordinates
            const rect = container.getBoundingClientRect();
            const graphX = centerX - rect.left;
            const graphY = centerY - rect.top;

            graph.zoomTo(constrainedZoom, { x: graphX, y: graphY });
          }
        }
        e.preventDefault();
      }
    };

    const handleTouchEnd = (e) => {
      if (touchCount === 2 && e.touches.length < 2) {
        // Pinch end
        initialDistance = 0;
        initialZoom = 1;
      }
      touchCount = e.touches.length;
    };

    // Add passive: false to allow preventDefault
    container.addEventListener("touchstart", handleTouchStart, {
      passive: false,
    });
    container.addEventListener("touchmove", handleTouchMove, {
      passive: false,
    });
    container.addEventListener("touchend", handleTouchEnd, { passive: false });

    return () => {
      container.removeEventListener("touchstart", handleTouchStart);
      container.removeEventListener("touchmove", handleTouchMove);
      container.removeEventListener("touchend", handleTouchEnd);
    };
  }, []);

  return (
    <div
      ref={containerRef}
      style={{
        width: "100%",
        height: "100%",
        touchAction: "none", // Prevent default touch behaviors
        WebkitUserSelect: "none", // Prevent text selection on iOS
        WebkitTouchCallout: "none", // Prevent callout on iOS
        WebkitTapHighlightColor: "transparent", // Remove tap highlight
        userSelect: "none", // Prevent text selection
        overflow: "hidden", // Prevent scrolling
      }}
    />
  );
};

function GraphPage() {
  const graphRef = React.useRef(null);
  const [isMobile, setIsMobile] = React.useState(false);

  const [type, setType] = React.useState("normal"); // main or categorise

  const [searchParams, setSearchParams] = useSearchParams();

  const userId = searchParams.get("uuid");
  const name = searchParams.get("name");

  const apiBaseURL =
    process.env.REACT_APP_API_BASE_URL || "http://localhost:7071/api";
  console.log("Using API Base URL:", apiBaseURL);

  // Detect mobile device and screen size
  React.useEffect(() => {
    const checkMobile = () => {
      const width = window.innerWidth;
      setIsMobile(
        width <= 768 ||
          /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
            navigator.userAgent
          )
      );
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);
    window.addEventListener("orientationchange", checkMobile);
    return () => {
      window.removeEventListener("resize", checkMobile);
      window.removeEventListener("orientationchange", checkMobile);
    };
  }, []);

  let nodeData = React.useMemo(
    () => ({
      nodes: [
        {
          id: "275f1687-e56b-4f0c-9bef-7618b613e0c3",
          size: 50,
          style: {
            fill: "#FFFFFFFF",
            labelText: "mohit",
            labelFill: "#FFFFFF",
            size: 50,
          },
          data: {
            user_id: "mohit",
            created: {
              low: -167459595,
              high: 407,
            },
            name: "mohit",
          },
          originalElementId: "4:53674b93-49ae-49e6-9ea3-f0d80256bc6a:0",
          originalLabels: ["app_user"],
          originalProperties: {
            user_id: "mohit",
            created: {
              low: -167459595,
              high: 407,
            },
            name: "mohit",
          },
        },
        {
          id: "8050590a-1403-4312-b0b0-52fada763b6c",
          size: 30,
          style: {
            fill: "#808080",
            labelText: "mystery_novels",
            labelFill: "#FFFFFF",
          },
          data: {
            user_id: "mohit",
            created: {
              low: -167459595,
              high: 407,
            },
            name: "mystery_novels",
          },
          originalElementId: "4:53674b93-49ae-49e6-9ea3-f0d80256bc6a:1",
          originalLabels: ["hobby"],
          originalProperties: {
            user_id: "mohit",
            created: {
              low: -167459595,
              high: 407,
            },
            name: "mystery_novels",
          },
          isLeaf: true,
        },
        {
          id: "f5916e54-dc48-447b-abad-7460ba43a15a",
          size: 30,
          style: {
            fill: "#808080",
            labelText: "dark_chocolate",
            labelFill: "#FFFFFF",
          },
          data: {
            user_id: "mohit",
            created: {
              low: -167458570,
              high: 407,
            },
            name: "dark_chocolate",
          },
          originalElementId: "4:53674b93-49ae-49e6-9ea3-f0d80256bc6a:2",
          originalLabels: ["preference"],
          originalProperties: {
            user_id: "mohit",
            created: {
              low: -167458570,
              high: 407,
            },
            name: "dark_chocolate",
          },
          isLeaf: true,
        },
        {
          id: "03d5cd75-5d54-45cc-ae8e-af10cd69b378",
          size: 30,
          style: {
            fill: "#9B59B6",
            labelText: "speaking_in_public",
            labelFill: "#FFFFFF",
          },
          data: {
            user_id: "mohit",
            created: {
              low: -167457581,
              high: 407,
            },
            name: "speaking_in_public",
          },
          originalElementId: "4:53674b93-49ae-49e6-9ea3-f0d80256bc6a:3",
          originalLabels: ["activity"],
          originalProperties: {
            user_id: "mohit",
            created: {
              low: -167457581,
              high: 407,
            },
            name: "speaking_in_public",
          },
          isLeaf: true,
        },
        {
          id: "8193a999-9a9c-4ad5-b6e5-4214c165f1cd",
          size: 40,
          style: {
            fill: "#E74C3C",
            labelText: "emotional connection",
            labelFill: "#FFFFFF",
            shape: "diamond",
          },
          data: {
            type: "relationship_sub_node",
            relationship_type: "emotional_connection",
            original_relationship_type: "prefers",
          },
          isRelationshipNode: true,
        },
        {
          id: "c7b68c2d-7ae4-429f-bba6-e1f29135e807",
          size: 40,
          style: {
            fill: "#F39C12",
            labelText: "activity relationship",
            labelFill: "#FFFFFF",
            shape: "diamond",
          },
          data: {
            type: "relationship_sub_node",
            relationship_type: "activity_relationship",
            original_relationship_type: "enjoys_reading",
          },
          isRelationshipNode: true,
        },
      ],
      edges: [
        {
          id: "67f74792-9dfe-46f1-98b4-960977ea652b",
          source: "275f1687-e56b-4f0c-9bef-7618b613e0c3",
          target: "8193a999-9a9c-4ad5-b6e5-4214c165f1cd",
          style: {
            labelText: "",
            stroke: "#979797FF",
            endArrow: true,
            endArrowType: "triangle",
            endArrowSize: 6,
          },
          originalElementId:
            "5:53674b93-49ae-49e6-9ea3-f0d80256bc6a:6917564212013170690",
          originalType: "prefers",
          originalProperties: {
            relationship_type: "emotional_connection",
            created: {
              low: -167458570,
              high: 407,
            },
          },
          edgeType: "to_relationship_node",
        },
        {
          id: "ea7e000b-6694-48e5-ae33-025c2cb62835",
          source: "8193a999-9a9c-4ad5-b6e5-4214c165f1cd",
          target: "f5916e54-dc48-447b-abad-7460ba43a15a",
          style: {
            labelText: "prefers",
            labelTextBaseline: "bottom",
            labelTextPlacement: "bottom",
            labelFill: "#FFFFFF",
            stroke: "#979797FF",
            endArrow: true,
            endArrowType: "triangle",
            endArrowSize: 6,
          },
          originalElementId:
            "5:53674b93-49ae-49e6-9ea3-f0d80256bc6a:6917564212013170690",
          originalType: "prefers",
          originalProperties: {
            relationship_type: "emotional_connection",
            created: {
              low: -167458570,
              high: 407,
            },
          },
          edgeType: "from_relationship_node",
        },
        {
          id: "574ce0c8-5287-4274-b621-3aa78bc48cf5",
          source: "275f1687-e56b-4f0c-9bef-7618b613e0c3",
          target: "c7b68c2d-7ae4-429f-bba6-e1f29135e807",
          style: {
            labelText: "",
            stroke: "#979797FF",
            endArrow: true,
            endArrowType: "triangle",
            endArrowSize: 6,
          },
          originalElementId:
            "5:53674b93-49ae-49e6-9ea3-f0d80256bc6a:1153034754304507904",
          originalType: "enjoys_reading",
          originalProperties: {
            relationship_type: "activity_relationship",
            created: {
              low: -167459595,
              high: 407,
            },
          },
          edgeType: "to_relationship_node",
        },
        {
          id: "cff0047a-02a9-4366-ac73-7d285b19c0af",
          source: "c7b68c2d-7ae4-429f-bba6-e1f29135e807",
          target: "8050590a-1403-4312-b0b0-52fada763b6c",
          style: {
            labelText: "enjoys reading",
            labelTextBaseline: "bottom",
            labelTextPlacement: "bottom",
            labelFill: "#FFFFFF",
            stroke: "#979797FF",
            endArrow: true,
            endArrowType: "triangle",
            endArrowSize: 6,
          },
          originalElementId:
            "5:53674b93-49ae-49e6-9ea3-f0d80256bc6a:1153034754304507904",
          originalType: "enjoys_reading",
          originalProperties: {
            relationship_type: "activity_relationship",
            created: {
              low: -167459595,
              high: 407,
            },
          },
          edgeType: "from_relationship_node",
        },
        {
          id: "25ef56dd-75e2-4436-a613-007a7af2429b",
          source: "8193a999-9a9c-4ad5-b6e5-4214c165f1cd",
          target: "03d5cd75-5d54-45cc-ae8e-af10cd69b378",
          style: {
            labelText: "feels anxious during",
            labelTextBaseline: "bottom",
            labelTextPlacement: "bottom",
            labelFill: "#FFFFFF",
            stroke: "#979797FF",
            endArrow: true,
            endArrowType: "triangle",
            endArrowSize: 6,
          },
          originalElementId:
            "5:53674b93-49ae-49e6-9ea3-f0d80256bc6a:6917644476361998339",
          originalType: "feels_anxious_during",
          originalProperties: {
            updated_at: {
              low: -167456627,
              high: 407,
            },
            relationship_type: "emotional_connection",
            created_at: {
              low: -167456627,
              high: 407,
            },
          },
          edgeType: "from_relationship_node",
        },
        {
          id: "52c464f1-61f1-405e-93c5-55b61de6f73a",
          source: "8193a999-9a9c-4ad5-b6e5-4214c165f1cd",
          target: "03d5cd75-5d54-45cc-ae8e-af10cd69b378",
          style: {
            labelText: "feels triggered by",
            labelTextBaseline: "bottom",
            labelTextPlacement: "bottom",
            labelFill: "#FFFFFF",
            stroke: "#979797FF",
            endArrow: true,
            endArrowType: "triangle",
            endArrowSize: 6,
          },
          originalElementId:
            "5:53674b93-49ae-49e6-9ea3-f0d80256bc6a:6917645575873626115",
          originalType: "feels_triggered_by",
          originalProperties: {
            relationship_type: "emotional_connection",
            created: {
              low: -167457581,
              high: 407,
            },
          },
          edgeType: "from_relationship_node",
        },
      ],
      summary: {
        totalNodes: 6,
        totalRelationships: 6,
        totalRelationshipSubNodes: 2,
      },
    }),
    []
  );

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["graph", userId, type], // Include type in query key
    queryFn: async () => {
      if (!userId) {
        return null;
      }

      try {
        const endpoint = "/graph";

        const response = await fetch(`${apiBaseURL}${endpoint}`, {
          method: "POST",
          cache: "no-cache",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ uuid: userId, name: name, type: type }),
        });

        if (!response.ok) {
          const errorText = await response.text();
          console.error("API response error:", {
            status: response.status,
            statusText: response.statusText,
            body: errorText,
          });
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        const nodes = await response.json();
        console.log("Response from API:", nodes);
        return nodes;
      } catch (fetchError) {
        console.error("Fetch error details:", {
          message: fetchError.message,
          stack: fetchError.stack,
          apiBaseURL,
          userId,
          userAgent: navigator.userAgent,
        });
        throw fetchError;
      }
    },
    enabled: !!userId && userId.trim() !== "", // Only run if userId is available and not empty
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    refetchOnWindowFocus: false, // Don't refetch when window regains focus
    refetchOnMount: false, // Don't refetch on component mount if data exists
    retry: (failureCount, error) => {
      // Retry up to 2 times, but not for 4xx errors
      if (error.message.includes("HTTP 4")) {
        return false;
      }
      return failureCount < 2;
    },
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });

  const graphOptions = React.useMemo(
    () => ({
      data: data || nodeData, // Use API data if available, otherwise fall back to static data
      //   canvas: {
      //     background: "#D60B0BFF",
      //   },
      background: "#1D1D1DE6",
      layout: {
        type: "d3-force",
        // Center force to keep nodes from drifting away - stronger centering
        center: {
          x: 0,
          y: 0,
          strength: isMobile ? 0.5 : 0.3, // Stronger centering force on mobile
        },
        // Collision detection to prevent node overlap
        collide: {
          radius: (d) => {
            return (d.size || 30) + (isMobile ? 10 : 20); // Add padding around nodes
          },
          strength: 0.8,
          iterations: 3,
        },
        // Link/edge forces
        link: {
          distance: (d) => {
            // Increase distances for more scattered effect
            if (d.source.data?.user_id !== undefined) {
              return isMobile ? 250 : 400; // Slightly reduced for better centering
            }
            if (d.source.isLeaf) {
              return isMobile ? 150 : 280; // Medium distances for leaf nodes
            }
            return isMobile ? 120 : 200; // Default distance
          },
          strength: (d) => {
            // Weaker link strength for more scattered layout
            if (d.source.isLeaf) {
              return 0.4;
            }
            return 0.6;
          },
        },
        // Many-body force (repulsion between nodes)
        manyBody: {
          strength: (d) => {
            // Stronger repulsion for more scattered effect but not too strong to lose centering
            if (d.isLeaf) {
              return isMobile ? -150 : -300;
            }
            if (d.isRelationshipNode) {
              return isMobile ? -120 : -250;
            }
            return isMobile ? -80 : -150; // Central nodes have less repulsion
          },
          distanceMin: isMobile ? 20 : 30,
          distanceMax: isMobile ? 250 : 400, // Reduced max distance for better centering
        },
        // Positioning force to spread nodes but keep them centered
        forceX: {
          strength: 0.02, // Reduced to allow more natural spreading
          x: 0,
        },
        forceY: {
          strength: 0.02, // Reduced to allow more natural spreading
          y: 0,
        },
        // Layout iterations and timing
        alpha: 0.6, // Reduced initial energy for faster settling
        alphaDecay: 0.05, // Faster cooling for quicker stabilization
        velocityDecay: 0.6, // More friction for faster settling
        iterations: isMobile ? 150 : 200, // Fewer iterations for faster initial render
      },
      behaviors: [
        "drag-canvas",
        {
          type: "zoom-canvas",
          enable: (event) => {
            // Check if it's a mobile/touch device
            const isTouchDevice =
              isMobile ||
              /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
                navigator.userAgent
              );

            if (!isTouchDevice) {
              // On PC: only zoom with Ctrl/Cmd key
              if (event.ctrlKey || event.metaKey) return true;
              return false;
            } else {
              // On mobile: always enable zoom
              return true;
            }
          },
          trigger: isMobile ? ["pinch"] : [],
        },
        { type: "drag-element-force" },
      ],
      plugins: [
        {
          key: "my-fullscreen",
          type: "fullscreen",
          autoFit: true,
        },
        {
          type: "toolbar",
          position: isMobile ? "bottom-left" : "top-right", // Better position on mobile
          size: isMobile ? "small" : "medium",
          style: {
            backgroundColor: "white",
            border: "1px solid #e5e5e5",
            borderRadius: "4px",
            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
          },
          getItems: () => [
            { id: "zoom-in", value: "zoom-in", icon: "+" },
            { id: "zoom-out", value: "zoom-out", icon: "-" },
            { id: "auto-fit", value: "auto-fit", icon: "⌂" },
            ...(isMobile ? [{ id: "reset", value: "reset", icon: "↻" }] : []),
          ],
          onClick: (value) => {
            const graph = graphRef.current;
            if (!graph) {
              console.warn("Graph is not initialized");
              return;
            }

            if (value === "zoom-in") {
              graph.zoomTo(graph.getZoom() * (isMobile ? 1.2 : 1.1));
            } else if (value === "zoom-out") {
              graph.zoomTo(graph.getZoom() * (isMobile ? 0.8 : 0.9));
            } else if (value === "auto-fit") {
              graph.fitView(isMobile ? { padding: 40 } : { padding: 80 });
            } else if (value === "reset") {
              graph.zoomTo(1);
              graph.translateTo(0, 0);
              // Re-center after reset
              setTimeout(() => {
                graph.fitView(isMobile ? { padding: 40 } : { padding: 80 });
              }, 100);
            }
          },
        },
      ],
      transforms: [
        {
          type: "process-parallel-edges",
          key: "process-parallel-edges",
          mode: "bundle",
          distance: isMobile ? 15 : 20, // Smaller distance on mobile
        },
      ],
      // Mobile-specific configurations
      ...(isMobile && {
        node: {
          style: {
            size: (d) => d.size * 0.8, // Smaller nodes on mobile
            labelFontSize: 10,
            labelMaxWidth: 60,
          },
        },
        edge: {
          style: {
            labelFontSize: 8,
            labelMaxWidth: 40,
            lineWidth: 1,
          },
        },
      }),
    }),
    [data, nodeData, isMobile]
  );

  return isLoading ? (
    <div className="flex items-center justify-center h-screen">
      <div className="loader text-white">
        Initiating {type === "categorise" ? "category" : "main"} graph...
      </div>
    </div>
  ) : error ? (
    <div className="flex items-center justify-center h-screen bg-gray-100 p-4">
      <div className="error-message bg-white p-6 rounded-lg shadow-lg max-w-md w-full">
        <h3 className="text-red-600 font-bold text-lg mb-2">
          Error Loading Graph
        </h3>
        <p className="text-gray-700 mb-3">
          {error.message || "Error loading graph data. Please try again later."}
        </p>

        {/* Debug information */}
        <div className="bg-gray-50 p-3 rounded text-xs text-gray-600 mb-4">
          <p>
            <strong>API URL:</strong> {apiBaseURL}
            {type === "categorise" ? "/categorise" : "/graph"}
          </p>
          <p>
            <strong>User ID:</strong> {userId || "Not provided"}
          </p>
          <p>
            <strong>Graph Type:</strong>{" "}
            {type === "categorise" ? "Category Graph" : "Main Graph"}
          </p>
          <p>
            <strong>Device:</strong> {isMobile ? "Mobile" : "Desktop"}
          </p>
          <p>
            <strong>Error:</strong> {error.message}
          </p>
        </div>

        <div className="flex gap-2">
          <button
            onClick={() => refetch()}
            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    </div>
  ) : (
    <div className={`w-full h-screen relative overflow-hidden`}>
      {/* Toggle Button for Graph Type */}
      <div className="absolute top-4 left-4 z-40">
        <div className="bg-white rounded-md shadow-md p-0.5 flex">
          <button
            onClick={() => setType("normal")}
            className={`px-2 py-1 rounded text-xs font-medium transition-colors ${
              type === "normal"
                ? "bg-blue-500 text-white"
                : "text-gray-700 hover:bg-gray-100"
            }`}
          >
            Normal
          </button>
          <button
            onClick={() => setType("category")}
            className={`px-2 py-1 rounded text-xs font-medium transition-colors ${
              type === "category"
                ? "bg-blue-500 text-white"
                : "text-gray-700 hover:bg-gray-100"
            }`}
          >
            Category
          </button>
        </div>
      </div>

      {/* Mobile info overlay */}
      {isMobile && (
        <div className="mobile-info-overlay absolute bottom-10 left-4 right-4 z-30 bg-black bg-opacity-70 text-white p-3 rounded-lg text-xs backdrop-blur-sm">
          <div className="flex items-center justify-between">
            <span>📱 Touch controls enabled</span>
            <button
              onClick={() => {
                const overlay = document.querySelector(".mobile-info-overlay");
                if (overlay) overlay.style.display = "none";
              }}
              className="text-white opacity-70 hover:opacity-100"
            >
              ✕
            </button>
          </div>
          <div className="mt-1 text-xs opacity-80">
            Drag to pan • Two-finger pinch to zoom • Double-tap to fit view
          </div>
        </div>
      )}

      <Graph
        options={graphOptions}
        onRender={(graph) => {
          graphRef.current = graph;
          console.log("Graph rendered:", graph);

          // Immediate centering on render
          if (graph && !graph.destroyed) {
            graph.fitView({ padding: isMobile ? 40 : 80 });
          }

          // Mobile-specific optimizations
          if (isMobile) {
            // Optimize performance for mobile
            graph.updatePlugin("my-fullscreen", {
              autoFit: true,
              padding: 40,
            });

            // Prevent context menu on long press
            graph.on("canvas:contextmenu", (e) => {
              e.preventDefault();
            });

            // Quick re-center after initial layout stabilizes on mobile
            setTimeout(() => {
              if (graph && !graph.destroyed) {
                graph.fitView({ padding: 40 });
              }
            }, 100);
          } else {
            // Quick re-center after initial layout stabilizes on desktop
            setTimeout(() => {
              if (graph && !graph.destroyed) {
                graph.fitView({ padding: 80 });
              }
            }, 200);
          }

          // Listen for layout end event for final centering
          graph.on("afterlayout", () => {
            if (graph && !graph.destroyed) {
              graph.fitView({ padding: isMobile ? 40 : 80 });
            }
          });
        }}
        onDestroy={() => {
          graphRef.current = null;
          console.log("Graph destroyed");
        }}
      />
    </div>
  );
}

export default GraphPage;
